import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { cn, Gallery, NavDots, Scroller, YandexShare } from '@divlab/divanui';
import { IconShare } from '@divlab/divanui/icons';

import * as PageProductStore from '@Stores/PageProduct';
import * as ModalWindows from '@Stores/ModalWindows';
import { useDeps } from '@Contexts/DI';
import { useRequest } from '@Contexts/Request';
import ApiProduct from '@Api/Product';
import useMedias from '@Hooks/useMedias';
import Img from '@UI/Img';
import ProductLike from '@Components/ProductLike';
import ProductImage from '@Pages/PageProduct/elements/ProductImage';
import Video from '@Pages/PageProduct/elements/Video';
import VideoPreview from '@Pages/PageProduct/elements/VideoPreview';
import ModelViewerAr from '../ModelViewerAr';
import ButtonAr from '../ButtonAr';
import Buttons from '../Buttons';
import LazyProductModel from '../ProductModel/LazyProductModel';
import Back from '../Back';
import styles from './MediaGallery.module.css';

import type { FC, HTMLAttributes, PropsWithChildren } from 'react';
import type { AR } from '@Types/AR';
import type { IGalleryRef, SwiperClass } from '@divlab/divanui';
import type { ProductData } from '@Types/Product';
import type { ConfigurationData, GalleryItem } from '@Pages/PageProduct/typings';

export interface MediaGalleryProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  images: GalleryItem[];
  ar?: AR;
  product: ProductData;
  configuration: ConfigurationData;
  productId: number;
}

export interface ItemsProps {
  className?: string;
  onChangeCurrent?(index: number): void;
}

interface ShareButtonProps {
  title: string;
  text?: string;
  url: string;
}

const ShareButton = (props: ShareButtonProps) => {
  const { title, text, url } = props;
  const { logger } = useDeps();

  const handleShare = async () => {
    try {
      await navigator.share({
        title,
        text,
        url,
      });
    } catch (error) {
      logger.error('Ошибка при попытке поделиться:', error);
    }
  };

  return <IconShare onClick={handleShare} />;
};

const Items: FC<PropsWithChildren<ItemsProps>> = (props) => {
  const { children, onChangeCurrent, ...restProps } = props;
  const { isDesktop } = useMedias();

  const handleSlideChange = useCallback(
    (swiper: SwiperClass) => {
      onChangeCurrent?.(swiper.realIndex);
    },
    [onChangeCurrent],
  );

  return isDesktop ? (
    <Gallery {...restProps} onSlideChange={handleSlideChange}>
      {children}
    </Gallery>
  ) : (
    <Scroller {...restProps} invisible>
      {children}
    </Scroller>
  );
};

const MediaGallery: FC<MediaGalleryProps> = (props) => {
  const { ar, images, product, configuration, productId, ...restProps } = props;
  const { logger } = useDeps();
  const { device } = useRequest();
  const { isMobileM, isMobile } = useMedias();
  const [slideIndex, setSlideIndex] = useState(0);
  const [url, setUrl] = useState(null);
  const [productsOnPins, setProductsOnPins] = useState<ProductData[]>(null);
  const idViewerModel = 'pageProduct';
  const viewerModel = PageProductStore.useViewerModel(idViewerModel);
  const { image, cylindo, constructorHash, id, parameterValues } = configuration;
  const hasCylindo = !!cylindo;
  const mainGalleryRef = useRef<IGalleryRef>(null);

  const needAppShare = useMemo(() => {
    if (!device || !navigator) return false;

    return (
      (device.device?.type === 'smartphone' || device.device?.type === 'tablet') && navigator.share
    );
  }, [device]);

  const isOpen360 = useMemo(() => {
    return isMobileM ? viewerModel?.isOpen360 : hasCylindo && slideIndex === 0;
  }, [hasCylindo, isMobileM, slideIndex, viewerModel?.isOpen360]);

  const allPhotos = useMemo(() => {
    let photos = images.filter((photo) => !!photo.src);

    if (hasCylindo && !isMobileM) {
      const firstPhoto = photos.find((photo) => photo.type === 'image');

      if (firstPhoto) {
        photos = [{ ...firstPhoto, isCylindo: true }, ...photos];
      }
    }

    return photos || [];
  }, [hasCylindo, images, isMobileM]);

  const allPinProductsIds = useMemo(() => {
    const productIds = [];

    allPhotos.forEach((photo) => {
      (photo.products || []).forEach((pr) => productIds.push(pr.id));
    });

    const uniqProductsIds = Array.from(new Set(productIds));

    return uniqProductsIds;
  }, [allPhotos]);

  const load = useCallback(async () => {
    try {
      if (allPinProductsIds.length === 0) return;

      const res = await ApiProduct.getProducts(allPinProductsIds);

      if (res.length === 0) {
        throw Error("Product wasn't loaded");
      }
      setProductsOnPins(res);
    } catch (err) {
      logger.log(err);
    }
  }, [allPinProductsIds, logger]);

  const handleClickImage = useCallback(
    (startSlideIndex: number) => {
      if (window.cancelClick) return;

      const filteredImages = allPhotos
        .map((item, index) => {
          if (!isMobileM && hasCylindo && index === 0) return;

          switch (item.type) {
            case 'video': {
              return {
                image: item.poster,
                video: item.src,
              };
            }

            case 'image': {
              return {
                image: item.src,
                video: null,
              };
            }

            case 'shortVideo': {
              return {
                image: null,
                video: item.src,
              };
            }
          }
        })
        .filter(Boolean);

      ModalWindows.open('ProductPhotos', {
        images: filteredImages,
        startSlideIndex: hasCylindo ? startSlideIndex - 1 : startSlideIndex,
      });
    },
    [allPhotos, hasCylindo, isMobileM],
  );

  const handleClickVideo = useCallback(() => {
    if (window.cancelClick) return;

    ModalWindows.open('Video', {
      videoId: allPhotos[slideIndex].src,
    });
  }, [allPhotos, slideIndex]);

  const handleSlideChange = useCallback((swiper: SwiperClass) => {
    setSlideIndex(swiper.realIndex);
  }, []);

  const handleChangeCurrent = useCallback((index: number) => {
    if (!mainGalleryRef.current) return;

    setSlideIndex(index);
    mainGalleryRef.current.slideTo(index);
  }, []);

  useEffect(() => {
    if (!window) return;
    setUrl(window.location.href);
  }, [configuration.url]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div {...restProps} className={cn(styles.photogallery)}>
      <div className={styles.topPanel}>
        <div className={styles.back}>
          <Back />
        </div>
        <div className={styles.right}>
          {isMobileM && (
            <div className={styles.wrapperIcon}>
              {needAppShare ? (
                <ShareButton url={url} title={`${product.type} ${configuration.name}`} />
              ) : (
                <YandexShare
                  services={['telegram', 'whatsapp', 'vkontakte']}
                  slotTrigger={<IconShare />}
                  url={url}
                  title={`${product.type} ${configuration.name}`}
                />
              )}
            </div>
          )}

          <div className={styles.wrapperIcon}>
            <ProductLike
              product={{
                ...product,
                id,
                constructorParameterValues: parameterValues as any,
              }}
              hash={constructorHash}
            />
          </div>
        </div>
      </div>
      <div className={styles.containerImages}>
        {!isMobileM && ar && <ButtonAr className={styles.button} idViewerModel={idViewerModel} />}
        <div className={styles.galleryContainer}>
          {!isMobileM && ar && (
            <CSSTransition
              timeout={200}
              classNames={{
                enter: styles.enter,
                enterActive: styles.enterActive,
                exit: styles.exit,
                exitActive: styles.exitActive,
              }}
              in={viewerModel?.isOpenAr}
              unmountOnExit
            >
              <div className={styles.modelViewerArWrapper}>
                <ModelViewerAr
                  className={styles.modelViewerAr}
                  ar={ar}
                  view='gallery'
                  idViewerModel={idViewerModel}
                />
              </div>
            </CSSTransition>
          )}
          {hasCylindo && (
            <LazyProductModel
              className={cn(styles.cylindo, { [styles.isActive]: isOpen360 })}
              medias={cylindo}
            />
          )}
          <Gallery ref={mainGalleryRef} slidesPerView={1} onSlideChange={handleSlideChange}>
            {allPhotos.map((item, index) => {
              const renderContent = () => {
                switch (item.type) {
                  case 'video':
                    return (
                      <VideoPreview
                        active
                        image={item.poster}
                        cnPreviewHeight={styles.videoHeight}
                        onClick={handleClickVideo}
                      />
                    );
                  case 'shortVideo':
                    return (
                      <Video
                        src={item.src}
                        cnPreviewHeight={styles.videoHeight}
                        onClick={() => handleClickImage(index)}
                      />
                    );
                  case 'image':
                    if (item.isCylindo) {
                      return <div />;
                    }
                    return (
                      <ProductImage
                        img={{ image: item.src, products: item.products, orientation: 'landscape' }}
                        size='m'
                        products={productsOnPins}
                        onClickImage={() => handleClickImage(index)}
                        loading='lazy'
                        productId={productId}
                      />
                    );
                }
              };

              return (
                <div key={`${item.src}_${item.isCylindo}_gallery`} className={styles.slide}>
                  {renderContent()}
                </div>
              );
            })}
            ;
          </Gallery>
        </div>

        {allPhotos.length > 1 && isMobileM && (
          <div className={styles.wrapperDots}>
            <NavDots count={allPhotos.length} slideIndex={slideIndex} view='filled' />
          </div>
        )}
        <Items className={styles.items}>
          {allPhotos.map((item, index) => {
            const isActive = slideIndex === index;
            const containerClassName = cn(styles.containerImage, {
              [styles.firstImage]: index === 0,
              [styles.active]: isActive,
            });

            const renderMedia = () => {
              if (item.type === 'shortVideo' || item.type === 'video') {
                return (
                  <VideoPreview
                    src={item.src}
                    cnPreviewHeight={styles.videoHeight}
                    active={isActive}
                    image={item.poster}
                  />
                );
              }

              return (
                <Img
                  itemProp='image'
                  alt=''
                  className={cn(styles.imageWrapper, {
                    [styles.cylindoThumb]: item.isCylindo,
                  })}
                  cnImage={styles.image}
                  src={item.isCylindo ? image || item.src : item.src}
                  loading={index !== 0 ? 'lazy' : 'eager'}
                />
              );
            };

            return (
              <div
                className={containerClassName}
                key={`${item.src}_${item.isCylindo}`}
                onClick={() => handleChangeCurrent(index)}
              >
                {renderMedia()}
              </div>
            );
          })}
        </Items>
      </div>

      {isMobileM && (
        <Buttons
          ar={ar}
          className={styles.buttons}
          scrollable={isMobile}
          size='s'
          idViewerModel={idViewerModel}
          hasCylindo={hasCylindo}
        />
      )}
    </div>
  );
};

export default memo(MediaGallery);
